/**
 * axes (#2388) — chart-axis gating for a single item's price series.
 *
 * A control value (a side / quality / channel) is only offered when at least
 * one series with observed points backs it. The chart disables every control
 * that has no backing data. Selecting a (side, quality?, channel?) returns the
 * VISIBLE series — possibly several distinct counterparties (multi-source),
 * which the chart draws as separate toggleable lines and NEVER averages.
 *
 * Pure + unit-tested. "Backed by data" always means ``points.length > 0``.
 */
import { CHANNELS, PRICE_SIDES, QUALITIES, type Channel, type PriceSide, type Quality } from './catalog';
import type { PriceSeries } from './types';

const withData = (series: PriceSeries[]): PriceSeries[] => series.filter((s) => s.points.length > 0);

/** Sides that have at least one backing series (ordered kauf, verkauf). */
export function availableSides(series: PriceSeries[]): PriceSide[] {
  const present = new Set(withData(series).map((s) => s.side));
  return PRICE_SIDES.filter((s) => present.has(s));
}

/**
 * Qualities backed by data (ordered; empty ⇒ no quality axis is live).
 *
 * `side` is OPTIONAL since HOF-6 removed the Kauf/Verkauf switcher: the chart
 * draws both sides at once, so the qualities it can offer are the ones backed on
 * EITHER. Narrowing to one side would hide a Qualität that only the purchase
 * series carries, and the reader has no switcher left with which to reach it.
 * The page still passes a side where it is scoping one (the tile grid).
 */
export function availableQualities(series: PriceSeries[], side?: PriceSide): Quality[] {
  const present = new Set(
    withData(series)
      .filter((s) => (side == null || s.side === side) && s.quality != null)
      .map((s) => s.quality as Quality),
  );
  return QUALITIES.filter((q) => present.has(q));
}

/** Channels backed by data for the given side (ordered; empty ⇒ no channel axis is live). */
export function availableChannels(series: PriceSeries[], side: PriceSide): Channel[] {
  const present = new Set(
    withData(series)
      .filter((s) => s.side === side && s.channel != null)
      .map((s) => s.channel as Channel),
  );
  return CHANNELS.filter((c) => present.has(c));
}

export interface SeriesSelection {
  /** Omitted ⇒ BOTH sides, which is what the chart draws since HOF-6. */
  side?: PriceSide;
  quality?: Quality;
  channel?: Channel;
}

/**
 * The VISIBLE series for a selection — every backing series matching the side
 * and any pinned quality/channel. Multiple results = a multi-source item (one
 * distinct line per counterparty); sorted by counterparty (de) for a stable
 * legend order.
 */
export function selectVisibleSeries(series: PriceSeries[], selection: SeriesSelection): PriceSeries[] {
  return withData(series)
    .filter((s) => selection.side == null || s.side === selection.side)
    .filter((s) => selection.quality == null || s.quality === selection.quality)
    .filter((s) => selection.channel == null || s.channel === selection.channel)
    .sort((a, b) => a.counterparty.localeCompare(b.counterparty, 'de'));
}

/** True when the item has any observed data at all (drives the "Keine Daten" tile). */
export function hasAnyData(series: PriceSeries[]): boolean {
  return withData(series).length > 0;
}

/**
 * A safe default control selection for an item: the first available side, then
 * (only if that axis is live) the first available quality / channel. Returns
 * null when the item has no data.
 */
export function defaultSelection(series: PriceSeries[]): SeriesSelection | null {
  const sides = availableSides(series);
  if (sides.length === 0) return null;
  const side = sides[0];
  const qualities = availableQualities(series, side);
  const channels = availableChannels(series, side);
  return {
    side,
    ...(qualities.length > 0 ? { quality: qualities[0] } : {}),
    ...(channels.length > 0 ? { channel: channels[0] } : {}),
  };
}
